import { damp, clamp, TAU } from '../math/mathx.js';

/**
 * @file `CameraRig` — the damped follow camera with 90-degree yaw snaps.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY A DEADZONE *AND* A SPRING
 * ─────────────────────────────────────────────────────────────────────────────
 * A pure spring follow means the camera is always moving, and at 52 degrees of
 * pitch a permanently drifting frame makes the whole city look like it is on a
 * boat. The deadzone (0.6 m) lets the player fidget without the world sliding;
 * once they commit to a direction the spring takes over and the camera glides.
 * The 0.25 s velocity look-ahead then biases the frame toward where they are
 * GOING, which is what buys reaction time in a top-down game.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * YAW IS FREE NOW — WHAT THAT COST
 * ─────────────────────────────────────────────────────────────────────────────
 * Yaw used to be snapped and never free: Q/E animated a 90-degree turn and
 * landed on an EXACT multiple of the step. That was not a stylistic choice — at
 * exact yaws an impostor atlas row is not an approximation of the mesh, it IS
 * the mesh, so tree LOD swaps were invisible.
 *
 * The owner asked for free rotation, so {@link CameraRig#spinYaw} and
 * {@link CameraRig#rotateYawBy} now move the camera to arbitrary angles and
 * {@link CameraRig#rotateYawStep} is kept for anything that still wants an exact
 * landing. The consequence, paid for in `levels/forest`: impostor rows became
 * approximations, so the forest doubled `impostorYaws` to 16 (11.25 degrees of
 * worst-case error) and re-selects rows whenever the camera crosses a row
 * boundary rather than only when a snap completes.
 *
 * `bus.emit('camera:yaw', yaw)` still fires when a snap completes AND, while
 * spinning, at most once per {@link CameraRig#yawEmitStep} radians of travel —
 * emitting every frame would run every listener's re-selection pass at 60 Hz.
 *
 * The rig owns no Three objects: it writes `engine.target/yaw/pitch/distance`
 * and calls `engine.updateCamera()`. Anything else (an editor, a cutscene) can
 * drive the same fields the same way.
 *
 * @module engine/actors/CameraRig
 */

const HALF_PI = Math.PI / 2;

export class CameraRig {
  /**
   * @param {import('../core/Engine.js').Engine} engine
   * @param {Object} [o]
   * @param {number} [o.pitchDeg=52] Camera pitch below horizontal.
   * @param {number} [o.distance=34] Metres from the target.
   * @param {number} [o.lookAhead=0.25] Seconds of velocity to lead by.
   * @param {number} [o.deadZone=0.6] Metres of slack before the camera reacts.
   * @param {number} [o.damp=9] Follow stiffness (`mathx.damp` lambda).
   * @param {number} [o.height=0.9] Metres above the player's feet to aim at —
   *   framing the chest, not the shoes.
   * @param {number} [o.yaw] Initial yaw, radians. Defaults to the engine's.
   * @param {number} [o.snapStep=Math.PI/2] Yaw step for {@link rotateYawStep}.
   * @param {number} [o.snapOffset=0] Added to every snap target. 0 keeps snaps
   *   on exact multiples of the step (the gameplay contract); a level that wants
   *   a diagonal 3/4 framing can pass `Math.PI/4`.
   * @param {number} [o.snapTime=0.3] Seconds per 90-degree turn.
   * @param {number} [o.spinRate=2.0] Radians/second for held free rotation
   *   (~115 deg/s: a full turn in 3.1 s, fast enough to be useful and slow
   *   enough that the world does not smear at 52 degrees of pitch).
   * @param {number} [o.spinAccel=12] Ramp for {@link spinYaw}, 1/s. A hard start
   *   reads as a jolt; ~0.15 s to full speed reads as a push.
   * @param {number} [o.yawEmitStep=Math.PI/8] Radians of free rotation between
   *   `camera:yaw` emissions. Match it to the finest consumer's row step.
   * @param {number} [o.driveBoost=8] Metres ADDED to `distance` while
   *   {@link setDriveMode} is on. A car covers ground three times faster than a
   *   walk, so the same framing shows a third of the reaction time.
   * @param {number} [o.driveLookAhead=0.45] Seconds of velocity to lead by
   *   while driving. Nearly double the on-foot 0.25 for the same reason.
   * @param {number} [o.modeDamp=2.6] How fast the two numbers above glide when
   *   the mode flips. Slow enough that getting into a car reads as the camera
   *   easing back, fast enough to be settled before the first corner.
   * @param {number} [o.interiorPull=-14] Metres ADDED to `distance` while
   *   {@link setInteriorMode} is on (negative — a room is a few metres across,
   *   not a city block). Adds to `driveBoost` rather than replacing it, so the
   *   two flags cannot fight; `minDistance` still floors the result.
   * @param {number} [o.interiorMax=26] Ceiling on the LIVE distance while
   *   interior mode is on. `lookAhead` and the pitch are untouched.
   * @param {number} [o.minDistance=8]
   * @param {number} [o.maxDistance=90]
   * @param {import('../types.js').Bounds2|null} [o.bounds] Clamp the target to
   *   the playable extent so the camera never sails off the level.
   */
  constructor(engine, o = {}) {
    const {
      pitchDeg = 52,
      distance = 34,
      lookAhead = 0.25,
      deadZone = 0.6,
      damp: lambda = 9,
      height = 0.9,
      yaw = null,
      snapStep = HALF_PI,
      snapOffset = 0,
      snapTime = 0.3,
      spinRate = 2.0,
      spinAccel = 12,
      yawEmitStep = Math.PI / 8,
      driveBoost = 8,
      driveLookAhead = 0.45,
      modeDamp = 2.6,
      interiorPull = -14,
      interiorMax = 26,
      minDistance = 8,
      maxDistance = 90,
      bounds = null,
    } = o;

    /** @type {import('../core/Engine.js').Engine} */
    this.engine = engine;
    this.pitch = pitchDeg * (Math.PI / 180);
    this.distance = clamp(distance, minDistance, maxDistance);
    this.lookAhead = lookAhead;
    this.deadZone = deadZone;
    this.damp = lambda;
    this.height = height;
    this.snapStep = snapStep;
    this.snapOffset = snapOffset;
    this.snapTime = Math.max(1e-3, snapTime);
    this.spinRate = spinRate;
    this.spinAccel = spinAccel;
    this.yawEmitStep = Math.max(1e-3, yawEmitStep);
    this.driveBoost = driveBoost;
    this.driveLookAhead = driveLookAhead;
    this.modeDamp = modeDamp;
    this.interiorPull = interiorPull;
    this.interiorMax = interiorMax;
    this.minDistance = minDistance;
    this.maxDistance = maxDistance;
    this.bounds = bounds;

    /**
     * The distance the ON-FOOT camera wants. `distance` is the LIVE value and
     * glides toward `baseDistance + driveBoost` in drive mode; `[` / `]` move
     * the base, so a zoom set on foot survives a car journey.
     */
    this.baseDistance = this.distance;
    this.baseLookAhead = this.lookAhead;
    this._drive = false;
    this._interior = false;

    /** @type {number} Live yaw in radians — read it every frame; it animates. */
    this.yaw = yaw ?? engine?.yaw ?? 0;

    /** Smoothed aim point (world). */
    this.target = { x: 0, y: 0, z: 0 };

    this._yawFrom = this.yaw;
    this._yawTo = this.yaw;
    this._yawT = 1;
    /** Live spin speed, rad/s — ramped toward `spinRate * dir` by `spinYaw`. */
    this._spin = 0;
    /** Yaw at the last `camera:yaw` emission; throttles the free-rotation ones. */
    this._emittedYaw = this.yaw;
    this._disposed = false;

    this._push();
  }

  /** @returns {boolean} True while a Q/E turn is animating. */
  get turning() {
    return this._yawT < 1;
  }

  // ── follow ─────────────────────────────────────────────────────────────────

  /**
   * @param {{x: number, y: number, z: number}} playerPos Feet position.
   * @param {{x: number, y?: number, z: number}|null} [playerVel] For look-ahead.
   * @param {number} dt Seconds.
   */
  follow(playerPos, playerVel, dt) {
    if (this._disposed || !playerPos) return;

    this._advanceYaw(dt);
    this._advanceMode(dt);

    const ahead = this.lookAhead;
    let dx = playerPos.x + (playerVel?.x ?? 0) * ahead;
    let dz = playerPos.z + (playerVel?.z ?? 0) * ahead;
    const dy = playerPos.y + this.height;

    if (this.bounds) {
      dx = clamp(dx, this.bounds.minX, this.bounds.maxX);
      dz = clamp(dz, this.bounds.minZ, this.bounds.maxZ);
    }

    // Deadzone: chase the point `deadZone` metres SHORT of the desired aim, so
    // the camera settles with the player somewhere inside the slack circle
    // instead of pinned dead centre.
    const ox = dx - this.target.x;
    const oz = dz - this.target.z;
    const dist = Math.hypot(ox, oz);
    let gx = this.target.x;
    let gz = this.target.z;
    if (dist > this.deadZone) {
      const k = (dist - this.deadZone) / dist;
      gx = this.target.x + ox * k;
      gz = this.target.z + oz * k;
    }

    this.target.x = damp(this.target.x, gx, this.damp, dt);
    this.target.z = damp(this.target.z, gz, this.damp, dt);
    // Y has no deadzone: on a heightfield the ground moves under you constantly
    // and a vertical deadzone reads as the camera hitching over every bump.
    this.target.y = damp(this.target.y, dy, this.damp, dt);

    this._push();
  }

  /**
   * Places the camera instantly (level entry, respawn, teleport) — no glide.
   * @param {{x: number, y: number, z: number}} playerPos
   */
  snapTo(playerPos) {
    if (!playerPos) return;
    this.target.x = playerPos.x;
    this.target.y = playerPos.y + this.height;
    this.target.z = playerPos.z;
    this._push();
  }

  // ── yaw ────────────────────────────────────────────────────────────────────

  /**
   * Starts a smooth 90-degree turn. Pressing again mid-turn queues another step
   * (the target accumulates) rather than restarting from the current angle.
   *
   * @param {number} dir `+1` or `-1` (Q / E).
   * @returns {number} The yaw this turn will land on.
   */
  rotateYawStep(dir) {
    const step = this.snapStep;
    const s = dir >= 0 ? 1 : -1;
    // Quantise from the TARGET, not the live yaw: chained presses stay exact.
    const base = Math.round((this._yawTo - this.snapOffset) / step);
    this._yawTo = (base + s) * step + this.snapOffset;
    this._yawFrom = this.yaw;
    this._yawT = 0;
    return this._yawTo;
  }

  /**
   * Free rotation while a key is HELD. Call every frame with the held direction
   * (`0` when nothing is held — the ramp needs to hear about the release).
   *
   * Cancels any snap in flight: a player who grabs the camera mid-turn wants the
   * camera, not the animation.
   *
   * @param {number} dir `-1` / `0` / `+1`.
   * @param {number} dt Seconds.
   * @returns {boolean} True while the camera is actually moving.
   */
  spinYaw(dir, dt) {
    const want = (dir > 0 ? 1 : dir < 0 ? -1 : 0) * this.spinRate;
    this._spin = damp(this._spin, want, this.spinAccel, dt);
    // Below ~0.6 deg/s the camera is visually parked; snapping to 0 stops an
    // exponential tail from emitting yaw events forever.
    if (Math.abs(this._spin) < 0.01) {
      this._spin = 0;
      return false;
    }
    if (this._yawT < 1) {
      this._yawT = 1;
      this._yawTo = this.yaw;
      this._yawFrom = this.yaw;
    }
    this.rotateYawBy(this._spin * dt);
    return true;
  }

  /**
   * Adds a yaw delta immediately (mouse drag, gamepad stick, scripted pan).
   * @param {number} delta Radians.
   */
  rotateYawBy(delta) {
    if (!delta) return;
    this.yaw += delta;
    // Same rule as `spinYaw`: someone who grabs the camera mid-snap wants the
    // camera, not the animation. Without this the snap would keep ticking and
    // fire its completion event on a yaw the player has already moved past.
    this._yawT = 1;
    this._yawFrom = this.yaw;
    this._yawTo = this.yaw;
    this._push();
    // Throttled: consumers that re-select impostor rows do real work per event.
    if (Math.abs(this.yaw - this._emittedYaw) >= this.yawEmitStep) this._emitYaw();
  }

  /**
   * Jumps to a yaw with no animation (level entry, HUD compass).
   * @param {number} yaw Radians.
   */
  setYaw(yaw) {
    this.yaw = yaw;
    this._yawFrom = yaw;
    this._yawTo = yaw;
    this._yawT = 1;
    this._push();
    this._emitYaw();
  }

  /**
   * @param {number} d Metres from the target. Sets the ON-FOOT base; while
   *   driving, the boost is added on top (so `[`/`]` do what they always did,
   *   whichever mode you are in).
   * @returns {number} The clamped distance actually applied.
   */
  setZoom(d) {
    this.baseDistance = clamp(d, this.minDistance, this.maxDistance);
    this.distance = this._capLive(this._wantDistance());
    this._push();
    return this.distance;
  }

  /**
   * Switches the rig between the walking framing and the DRIVING one: further
   * back, and leading the velocity harder. Both glide (see `modeDamp`); the
   * deadzone, the spring and the yaw are untouched, because on-foot feel is not
   * something a car should be allowed to renegotiate.
   * @param {boolean} on
   */
  setDriveMode(on) {
    this._drive = !!on;
  }

  /** @returns {boolean} */
  get driveMode() {
    return this._drive;
  }

  /**
   * Switches the rig into the INTERIOR framing: pulled right in, because a room
   * is a few metres across and the walking distance frames mostly floor. Glides
   * on the same `modeDamp`; the pitch, the deadzone and the look-ahead are
   * untouched (an interior is walked, so the on-foot lead is already right).
   * @param {boolean} on
   */
  setInteriorMode(on) {
    this._interior = !!on;
  }

  /** @returns {boolean} */
  get interiorMode() {
    return this._interior;
  }

  /**
   * @param {number} deg
   */
  setPitchDeg(deg) {
    this.pitch = deg * (Math.PI / 180);
    this._push();
  }

  /** Idempotent; the rig holds no GPU resources. */
  dispose() {
    this._disposed = true;
    this.engine = null;
  }

  // ── internals ──────────────────────────────────────────────────────────────

  /**
   * Glides `distance` and `lookAhead` toward whichever mode is current. Both
   * are cheap `damp` calls and both short-circuit once settled, so an on-foot
   * session pays one comparison a frame for the feature.
   * @param {number} dt
   */
  _advanceMode(dt) {
    const wantD = this._capLive(this._wantDistance());
    if (Math.abs(this.distance - wantD) > 1e-3) this.distance = damp(this.distance, wantD, this.modeDamp, dt);
    const wantA = this._drive ? this.driveLookAhead : this.baseLookAhead;
    if (Math.abs(this.lookAhead - wantA) > 1e-4) this.lookAhead = damp(this.lookAhead, wantA, this.modeDamp, dt);
  }

  /**
   * The distance the CURRENT modes want. The two offsets ADD rather than
   * override, so a frame where both flags are set (or neither) is still one
   * finite number — no branch can leave `distance` NaN or stuck.
   * @returns {number}
   */
  _wantDistance() {
    const d = this.baseDistance
      + (this._drive ? this.driveBoost : 0)
      + (this._interior ? this.interiorPull : 0);
    return clamp(d, this.minDistance, this.maxDistance);
  }

  /**
   * Ceiling on the LIVE distance while indoors. The rooms sit under a terrain
   * shelf that hides them from the street, so a camera pulled out past
   * `interiorMax` with `]` would rise through that shelf and frame the city
   * instead of the room.
   * @param {number} d
   * @returns {number}
   */
  _capLive(d) {
    return this._interior ? Math.min(d, this.interiorMax) : d;
  }

  /** @param {number} dt */
  _advanceYaw(dt) {
    if (this._yawT >= 1) return;
    this._yawT = Math.min(1, this._yawT + dt / this.snapTime);
    if (this._yawT >= 1) {
      // Assign the target EXACTLY (no lerp residue) — see the header note about
      // impostor rows.
      this.yaw = this._yawTo;
      this._yawFrom = this._yawTo;
      this._emitYaw();
      return;
    }
    const t = ease(this._yawT);
    this.yaw = this._yawFrom + (this._yawTo - this._yawFrom) * t;
  }

  _emitYaw() {
    // Normalised copy for consumers that index by yaw; `this.yaw` itself is left
    // unwrapped so chained turns keep animating the short way.
    this._emittedYaw = this.yaw;
    const bus = this.engine?.bus;
    if (bus?.emit) bus.emit('camera:yaw', ((this.yaw % TAU) + TAU) % TAU);
  }

  /** Writes the orbit onto the engine. */
  _push() {
    const e = this.engine;
    if (!e) return;
    e.target.set(this.target.x, this.target.y, this.target.z);
    e.yaw = this.yaw;
    e.pitch = this.pitch;
    e.distance = this.distance;
    e.updateCamera();
  }
}

/**
 * Smootherstep. Zero velocity at BOTH ends, which is what makes a snap read as
 * deliberate instead of as a lurch.
 * @param {number} t 0..1
 * @returns {number}
 */
function ease(t) {
  return t * t * t * (t * (t * 6 - 15) + 10);
}

export { HALF_PI };
